/**
 * Regras de Conformidade — ANM Res. 95/2022 + 220/2025
 * -----------------------------------------------------
 * Origem: Avaliação de Requisitos Rev.1 §3 (Grupo A), R-A01..A10.
 *
 * Funções puras. Recebem dados, retornam decisões.
 */

import type {
  Instrument,
  InstrumentThreshold,
  NivelEmergencia,
  Reading,
  TarpLevel,
  ThresholdBand,
} from '../types';

// ── R-A07: Vigência da Carta de Controle (GUPM-125) ─────────
/**
 * `true` se o valor está na banda meio-aberta `[min, max)` (lados `null` =
 * ilimitados). Meio-aberta no topo p/ casar a semântica monotônica legada
 * (`valor >= limiar → próximo nível`) e o SQL `classify_reading_level`.
 */
function inBand(value: number, band: ThresholdBand): boolean {
  if (band.min !== null && value < band.min) return false;
  if (band.max !== null && value >= band.max) return false;
  return true;
}

/**
 * Classifica uma leitura contra a Carta de Controle (bandas por nível),
 * respeitando a vigência (R-A07): antes de `validFrom` retorna `null`.
 *
 * Algoritmo único p/ nível d'água (monotônico) e deslocamento (bilateral
 * por eixo): as bandas são aninhadas (normal ⊂ atenção ⊂ alerta ⊂
 * emergência) e vence a banda mais interna (menos severa) que contém o
 * valor. Espelha `classify_reading_level` no SQL. O chamador resolve o
 * threshold do canal certo antes de chamar.
 */
export function classifyReadingLevel(
  threshold: InstrumentThreshold,
  value: number,
  ts: Date,
): TarpLevel | null {
  if (ts < threshold.validFrom) return null;
  if (inBand(value, threshold.normal)) return 'NORMAL';
  if (inBand(value, threshold.atencao)) return 'ATENCAO';
  if (inBand(value, threshold.alerta)) return 'ALERTA';
  // Em emergência, ou fora de qualquer banda conhecida → pior caso.
  return 'EMERGENCIA';
}

/**
 * Ponte do cadastro legado (colunas `level_*_max` em `instruments`, nível
 * d'água monotônico — GUPM-120) para a Carta de Controle por banda. Usada
 * enquanto a UI não consome `instrument_thresholds` direto. Bandas
 * unilaterais (min = -inf); `null` no topo = banda ilimitada.
 *
 * `null` quando o instrumento não tem limiar NENHUM (GUPM-234). Antes devolvia um
 * threshold cuja banda `normal` era `{min: null, max: null}` — ilimitada dos dois
 * lados, casando com qualquer valor: instrumento sem Carta era classificado
 * NORMAL, e o RT via verde ao digitar qualquer número no M6. Espelha o
 * `if not found then return null` do `classify_reading_level` no SQL (R-A07).
 */
export function thresholdFromInstrument(instrument: Instrument): InstrumentThreshold | null {
  const semNenhumLimiar =
    instrument.levelAtencaoMax == null &&
    instrument.levelAlertaMax == null &&
    instrument.levelEmergenciaMax == null;
  if (semNenhumLimiar) return null;
  return {
    instrumentId: instrument.id,
    canal: null,
    validFrom: instrument.thresholdsValidFrom,
    normal: { min: null, max: instrument.levelAtencaoMax },
    atencao: { min: null, max: instrument.levelAlertaMax },
    alerta: { min: null, max: instrument.levelEmergenciaMax },
    emergencia: { min: null, max: null },
  };
}

/** Retorna `true` se a leitura está dentro da vigência atual da carta de controle. */
export function isInVigencia(instrument: Instrument, ts: Date): boolean {
  return ts >= instrument.thresholdsValidFrom;
}

// ── R-A02: Retenção mínima 5 anos ───────────────────────────
export const RETENCAO_LOGS_ANOS = 5;
export const RETENCAO_LOGS_MS = RETENCAO_LOGS_ANOS * 365.25 * 24 * 60 * 60 * 1000;

export function isWithinRetentionWindow(eventTs: Date, now: Date = new Date()): boolean {
  return now.getTime() - eventTs.getTime() <= RETENCAO_LOGS_MS;
}

// ── R-A01: Polling mínimo 30s no Dashboard ──────────────────
/**
 * Piso do intervalo de polling — R-A01 diz `Polling Dashboard ≥ 30s`. É PISO, não
 * teto: pollar mais rápido que isso contraria o requisito. Para ter menos latência
 * sem mexer no número, o caminho é push (GUPM-244: `realtime.send` no banco).
 */
export const DASHBOARD_POLLING_MS = 30_000;

/**
 * Intervalo com a aba em segundo plano — GUPM-234.
 *
 * Existe porque o polling em aba escondida hoje é ZERO: o TanStack pausa o
 * `refetchInterval` quando o documento fica oculto. Somado ao broadcast quebrado
 * (tópico errado, corrigido neste card), a aba em segundo plano não recebia
 * atualização por nenhum caminho — e é assim que o plantão deixa o app aberto.
 *
 * Mais lento que o da aba visível de propósito: ninguém está olhando, e a sala de
 * controle não é o único cliente (mobile em campo entra depois, com 4G instável).
 */
export const POLLING_OCULTO_MS = 60_000;

/**
 * Intervalo de polling conforme a aba esteja visível ou oculta.
 *
 * Recebe `ocultaNaTela` como parâmetro em vez de ler `document`: este arquivo é
 * domínio puro (sem DOM, sem React, sem Supabase). Quem lê a visibilidade é
 * `src/lib/query-polling.ts`, na borda.
 */
export function pollingIntervalMs(ocultaNaTela: boolean): number {
  return ocultaNaTela ? POLLING_OCULTO_MS : DASHBOARD_POLLING_MS;
}

// ── NE-1 / NE-2 / NE-3 (declaração formal pelo Coord. PAEBM) ─
export const NIVEL_EMERGENCIA_LABEL: Record<NivelEmergencia, string> = {
  normal: 'Normal',
  'NE-1': 'NE-1 — Atenção',
  'NE-2': 'NE-2 — Alerta',
  'NE-3': 'NE-3 — Emergência',
};

/**
 * Mapa sugerido (não-vinculante) entre TarpLevel e NivelEmergencia.
 * A declaração formal de NE é responsabilidade do Coord. PAEBM —
 * essa função sugere, não decide. Telles: "devem ser estabelecidas
 * as relações entre NE-1/2/3 e atenção/alerta/emergência".
 */
export function sugerirNivelEmergencia(level: TarpLevel | null): NivelEmergencia {
  if (level === 'EMERGENCIA') return 'NE-3';
  if (level === 'ALERTA')     return 'NE-2';
  if (level === 'ATENCAO')    return 'NE-1';
  return 'normal';
}

// ── R-E04: FS Quinzenal — limiares NBR 13028 ─────────────────
/**
 * Limiares de Fator de Segurança quinzenal. FS abaixo do limiar
 * dispara alerta visual no M7.
 */
export const FS_LIMIAR_DRENADA = 1.5;
export const FS_LIMIAR_NAO_DRENADA = 1.3;

export interface FsQuinzenalInput {
  seccao: string;        // 'B-B''
  valor: number;
  condicao: 'drenada' | 'nao_drenada';
}

export function classifyFs(input: FsQuinzenalInput): 'normal' | 'alerta_fs' {
  const limiar = input.condicao === 'drenada' ? FS_LIMIAR_DRENADA : FS_LIMIAR_NAO_DRENADA;
  return input.valor < limiar ? 'alerta_fs' : 'normal';
}

// ── R-A03: Imutabilidade do audit_log ───────────────────────
/**
 * Eventos auditáveis (R-A03, R-A10). Lista NÃO-EXAUSTIVA — adicionar
 * conforme módulos forem implementados.
 */
export const EVENTOS_AUDITAVEIS = [
  'tarp.opened',
  'tarp.closed',
  'tarp.via_redirected',
  'reading.inserted_manual',
  'reading.inserted_api',
  'threshold.changed',
  'recomendacao.created',
  'recomendacao.status_changed',
  'banner.activated',
  'banner.resolved',
  'engineer.login',
  'engineer.logout',
] as const;

export type EventoAuditavel = typeof EVENTOS_AUDITAVEIS[number];

// ── Helper de severidade combinada ──────────────────────────
/**
 * Para o Dashboard M1: combina os níveis de várias leituras recentes
 * em um "estado geral" da estrutura. Pior nível ganha.
 */
export function nivelGeral(readings: Reading[]): TarpLevel {
  let pior: TarpLevel = 'NORMAL';
  const ranking: Record<TarpLevel, number> = { NORMAL: 0, ATENCAO: 1, ALERTA: 2, EMERGENCIA: 3 };
  for (const r of readings) {
    if (r.level === null) continue;
    if (ranking[r.level] > ranking[pior]) pior = r.level;
  }
  return pior;
}
